import { useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { IS_DEVELOPMENT, URL } from '../utils/envConstants.ts';
import { gatekeeper } from '../utils/gatekeeper.ts';
import { extractQueryFromCurrentRoute } from '../utils/utils.ts';

const useNavigateActions = () => {
  const navigate = useNavigate();

  const getFinalRoute = useCallback((market: string) => {
    const currentRouteUnknown = gatekeeper?.navigation?.getCurrentRoute?.();

    let pathname = '';
    let routeId: string | undefined = undefined;

    if (typeof currentRouteUnknown === 'object' && currentRouteUnknown !== null) {
      const cr = currentRouteUnknown as { pathname?: unknown; page?: unknown };
      pathname = typeof cr.pathname === 'string' ? cr.pathname : '';
      routeId =
        typeof cr.page === 'string' && cr.page.length > 0
          ? cr.page
          : undefined;
    }

    // Prefer resolved route by id for operational pages; fallback to pathname for landing pages
    const basePath: string = (
      routeId
        ? gatekeeper?.navigation?.getRoute?.(routeId, pathname)
        : pathname
    ) ?? pathname;
    const routeParts = basePath.split('/').filter(Boolean);

    return market === 'es' ? routeParts.join('/') : routeParts.slice(1).join('/');
  }, []);

  const constructUrl = useCallback(
    (
      market: string,
      routePath?: string,
      step?: number,
      includeQuery: boolean = false,
    ) => {
      const marketSegment = market === 'es' ? '' : `/${market}`;

      // Build base path first, without query
      if (!routePath) {
        const baseUrl = `${URL}${marketSegment}`;
        // Append query only for step navigation
        if (step !== undefined || includeQuery) {
          const currentRouteUnknown = gatekeeper?.navigation?.getCurrentRoute?.();
          const queryString = extractQueryFromCurrentRoute(currentRouteUnknown);

          const withQuery = queryString ? `${baseUrl}/?${queryString}` : `${baseUrl}/`;

          return step !== undefined ? `${withQuery}#register_${step}` : withQuery;
        }

        return baseUrl;
      }

      const baseUrl = `${URL}${marketSegment}/${routePath}/`;

      if (step !== undefined || includeQuery) {
        const currentRouteUnknown = gatekeeper?.navigation?.getCurrentRoute?.();
        const queryString = extractQueryFromCurrentRoute(currentRouteUnknown);

        const withQuery = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return step !== undefined ? `${withQuery}#register_${step}` : withQuery;
      }

      return baseUrl;
    },
    [],
  );

  const navigateTo = useCallback((market: string, step: number, inGuard?: boolean) => {
    if (!IS_DEVELOPMENT) {
      const finalRoute = getFinalRoute(market);

      window.location.href = market
        ? constructUrl(market, finalRoute, step)
        : `#/register_${step}`;

      return;
    }

    if (inGuard) {
      return <Navigate to={`/${market}/register_${step}`} replace />;
    }

    navigate(`/${market}/register_${step}`);
  }, [navigate, constructUrl, getFinalRoute]);

  const navigateToMarket = useCallback((market: string, inGuard?: boolean) => {

    if (!IS_DEVELOPMENT) {
      const finalRoute = getFinalRoute(market);

      if (finalRoute === 'register') {
        gatekeeper?.navigation.navigate('/', {});

        return;
      }

      sessionStorage.setItem('fabric:registration:affiliateWelcomeOffer', '');

      window.history.replaceState(null, '', constructUrl(market, finalRoute, undefined, true));

      return;
    }

    if (inGuard) {
      return <Navigate to={`/${market}`} replace />;
    }

    navigate(market, { replace: true });
  }, [navigate, constructUrl, getFinalRoute]);

  return { navigateTo, navigateToMarket, constructUrl };
};

export default useNavigateActions;
